import React, {
  useMemo,
  useState,
} from 'react';
import AutoCompletedInput, {
  Props as AutoCompleteProps,
} from 'core/components/AutoComplete';
import clsx from 'clsx';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  UserDTO,
} from 'dtos';
import ProviderLookupListDropdown from 'views/EMR/components/ProviderLookup/Dropdown';
import DropdownOptionRow from 'core/components/Lookup/DropdownOptionRow';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
/**
 * @description Config for inactive users
  * @property onlyActive - Whether to show inactive users
 * @default { onlyActive: false, allowSelectInactive: true }
 */
type ViewConfig = {
  onlyActive?: boolean;
  shouldFilterSystemUsers?: boolean;
};

type Props = Omit<AutoCompleteProps<any>, 'options' | 'getLabel'> & {
  onlyDoctors?: boolean;
  viewConfig?: ViewConfig;
};
/**
 * @default { onlyActive: true }
 */
export default function ProviderLookup({
  id,
  placeholder,
  className,
  onlyDoctors = true,
  blurOnSelect,
  inputRef,
  viewConfig,
  ...props
}: Props) {
  const { data: options, isLoading } = useGetUsers({ onlyDoctors });
  const {
    onlyActive = false,
    shouldFilterSystemUsers = false,
  } = viewConfig ?? {};
  const [showInactive, setShowInactive] = useState(false);

  const activeUsers = useMemo(
    () => options?.filter((option) => option?.isActive !== false) ?? [],
    [options],
  );
  const inactiveUsers = useMemo(
    () => options?.filter((option) => option?.isActive === false) ?? [],
    [options],
  );

  const usersList = useMemo(() => (
    showInactive
      ? [...activeUsers, ...inactiveUsers]
      : activeUsers
  ), [activeUsers, inactiveUsers, onlyActive, showInactive]);

  const filteredOptions = useMemo(() => {
    const _options = (
      onlyActive
        ? activeUsers
        : usersList
    );

    return shouldFilterSystemUsers
      ? _options?.filter((option) => !option?.isServiceAccount)
      : _options;
  }, [activeUsers, usersList, onlyActive, shouldFilterSystemUsers]);

  const inactiveConfig = useMemo(() => (
    (onlyActive || inactiveUsers?.length === 0)
      ? null
      : {
        showInactive,
        onToggleShowInactive: () => setShowInactive((prev) => !prev),
        type: onlyDoctors ? 'doctor' : 'user',
        count: inactiveUsers?.length,
      }
  ), [onlyActive, showInactive, onlyDoctors, inactiveUsers]);

  return (
    <AutoCompletedInput<UserDTO>
      {...props}
      id={id ?? 'doctor'}
      className={clsx('!w-full', className)}
      classes={{
        inputRoot: '!pr-7 !py-2 !flex-nowrap',
      }}
      openOnFocus
      loading={isLoading}
      popupIcon={isLoading ? <Spinner /> : undefined}
      disabled={isLoading}
      blurOnSelect={blurOnSelect}
      options={filteredOptions ?? []}
      renderValue={(selected) => {
        if (Array.isArray(selected)) {
          return (
            <span className="block max-w-full truncate whitespace-nowrap">
              {selected.map((user) => getDisplayName(user?.formattedNames)).join(', ')}
            </span>
          );
        }

        return (
          <span className="block max-w-full truncate whitespace-nowrap">
            {getDisplayName(selected?.formattedNames)}
          </span>
        );
      }}
      placeholder={(isNil(props?.value) || isEmpty(props?.value)) ? (placeholder ?? 'Select Doctor') : ''}
      slotProps={{
        listbox: {
          component: ProviderLookupListDropdown,
          inactiveConfig,
        },
      } as Object}
      getLabel={(val) => getDisplayName(val.formattedNames)}
      includeInputInList
      isOptionEqualToValue={(a, b) => a.userId === (b as UserDTO).userId}
      inputRef={inputRef}
      dropdownClassName="!overflow-hidden p-2"
      renderOption={(props, option) =>
        (
          <DropdownOptionRow
            key={option?.userId}
            id={option?.userId?.toString()}
            label={getDisplayName(option?.formattedNames, '--')}
            props={props}
          >
            <span className={clsx({
              'text-gray-400': !option?.isActive,
            })}
            >
              {getDisplayName(option?.formattedNames, '--')}
            </span>
          </DropdownOptionRow>
        )}
      InputProps={{
        classes: {
          input: 'populate-input-placeholder',
        },
      }}
    />
  );
}
